import { DateTime } from 'luxon';
import { BORDER_RADIUSES, SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import AttributeItem from 'dashboard/components/AttributeItem';
import { SimulatedDataSticker } from 'dashboard/components/SimulatedDataSticker/SimulatedDataSticker';
import AsyncSelfie from 'dashboard/components-lib/AsyncSelfie';
import {
  formatAddressFieldDisplay,
  formatDate,
  formatNormalizedPostalCode,
} from 'dashboard/lib/utils/formatters';
import { Account } from 'dashboard/models/account';
import { EnvironmentType } from 'dashboard/models/environment';
import { COLORS } from 'dashboard/styles/variables';
import Card from 'design-system/components/Card';
import ImageAvatar from 'images/dashboard/avatar.svg';
import { FieldString } from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

const AVATAR_WIDTH = 128;
const AVATAR_HEIGHT = 128;

const ColumnContainer = styled('div')`
  display: grid;
  grid-auto-flow: column;
  grid-gap: ${SPACINGS.base * 4}px ${SPACINGS.base * 2}px;
  grid-template-columns: ${AVATAR_WIDTH + SPACINGS.base * 2}px auto auto;
`;

const Column = styled('div')``;

const Avatar = styled('div')`
  border-radius: ${BORDER_RADIUSES.base};
  background-color: ${COLORS.gray8};
  width: ${AVATAR_WIDTH}px;
  height: ${AVATAR_HEIGHT}px;
`;

const ProfileInfo = styled('div')`
  grid-template-rows: auto auto;
  display: grid;
  gap: ${SPACINGS.base * 3}px ${SPACINGS.base * 2}px;
`;

interface AccountProfile {
  account: Account;
  environmentType: EnvironmentType;
  dataTest?: string;
}

const AccountProfile = ({ account, dataTest, environmentType }: AccountProfile) => {
  const fieldMapKeys = Object.keys(account.attributes['fields']);
  if (
    !fieldMapKeys.includes('selfie_photo') &&
    !fieldMapKeys.includes('name') &&
    !fieldMapKeys.includes('birthdate') &&
    !fieldMapKeys.includes('address')
  ) {
    return null;
  }

  const fieldAddress = account.attributes.fields['address']?.value as Record<string, FieldString>;

  const addressStreet1 = fieldAddress?.street_1?.value;
  const addressStreet2 = fieldAddress?.street_2?.value;
  const addressCity = fieldAddress?.city?.value;
  const addressSubdivision = fieldAddress?.subdivision?.value;
  const addressPostalCode = fieldAddress?.postal_code?.value;
  const addressCountryCode = fieldAddress?.country_code?.value;
  const birthdate = account.attributes.fields.birthdate?.value as Iso8601DateString;

  const {
    'name-full': nameFull,
    'name-full-localized': nameFullLocalized,
    'selfie-photo-url': selfiePhotoUrl,
  } = account.attributes;

  const formattedBirthdate =
    birthdate != null ? (
      <div>
        <p>{formatDate(birthdate)}</p>
      </div>
    ) : undefined;

  const formattedAge =
    birthdate != null ? (
      <p>{Math.floor(DateTime.local().diff(DateTime.fromISO(birthdate), 'years').years)}</p>
    ) : undefined;

  const normalizedPostalCode = formatNormalizedPostalCode({
    addressPostalCode: addressPostalCode,
    addressCountryCode: addressCountryCode,
  });

  const address = formatAddressFieldDisplay({
    addressStreet1: addressStreet1,
    addressStreet2: addressStreet2,
    addressCity: addressCity,
    addressSubdivision: addressSubdivision,
    addressPostalCode: normalizedPostalCode,
  });

  return (
    <Card data-test={dataTest}>
      <Card.Body>
        <Card.Title>
          Profile
          {environmentType === ApiModel.EnvironmentSandbox && (
            <SimulatedDataSticker dataSectionLabel='account-profile' />
          )}
        </Card.Title>
        <ColumnContainer>
          <Column>
            <div>
              <Avatar data-test='account-profile__avatar'>
                <AsyncSelfie
                  height={`${AVATAR_HEIGHT}px`}
                  width={`${AVATAR_WIDTH}px`}
                  value={selfiePhotoUrl ? [{ url: selfiePhotoUrl }] : []}
                  modalLabel='Profile'
                  placeholder={<ImageAvatar height={AVATAR_HEIGHT} width={AVATAR_WIDTH} />}
                  sourceToken={account.id}
                />
              </Avatar>
            </div>
          </Column>
          <Column>
            <ProfileInfo>
              <AttributeItem
                title='Name'
                value={nameFullLocalized ?? nameFull}
                isRedacted={false}
                placeholder='No name'
              />
              <AttributeItem
                title='Birthdate'
                value={formattedBirthdate}
                isRedacted={false}
                placeholder='No birthday'
              />
            </ProfileInfo>
          </Column>
          <Column>
            <ProfileInfo>
              <AttributeItem
                title='Address'
                value={address}
                isRedacted={false}
                placeholder='No address'
              />
              <AttributeItem
                title='Age'
                value={formattedAge}
                isRedacted={false}
                placeholder='&#8212;'
              />
            </ProfileInfo>
          </Column>
        </ColumnContainer>
      </Card.Body>
    </Card>
  );
};

export default AccountProfile;
